import { useUser } from '@clerk/clerk-react';
import { PullRecord } from '@mesa/core/pull';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { createContext, memo, useContext, useMemo, useRef, useState, Suspense, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomItemComponentProps, VList, VListHandle } from 'virtua';

import { MousedownLink } from '@/components/mousedown-link';
import { Button } from '@/components/ui/button';
import { DebouncedInput } from '@/components/ui/input';
import { CIStatusIcon } from '@/features/pulls/components/pull-checks-popover';
import {
  AuthorCell,
  ChangesCell,
  ReviewStatusCell,
  StateCell,
  stateDisplayName,
  TitleCell,
} from '@/features/pulls/components/pull-list';
import { PullListNoResults } from '@/features/pulls/components/pull-list-no-results';
import { PullSuggestionBanner } from '@/features/pulls/components/pull-suggestion-banner';
import { PullsFilter } from '@/features/pulls/components/pulls-filter';
import { SavedViewCreator } from '@/features/pulls/components/saved-view-creator';
import { SavedViewsDropdown } from '@/features/pulls/components/saved-views-dropdown';
import { usePullListShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { usePullsFilters } from '@/hooks/use-pulls-filters';
import { useSavedViews } from '@/hooks/use-saved-views';
import { getTimeAgo } from '@/lib/time';
import { cn } from '@/lib/utils';
import { createZeroInstance, useZero, useZeroQuery } from '@/lib/zero';
import { getCurrentOrg, useCurrentOrg } from '@/services/org';

const PAGE_SIZE = 500;

export const Route = createFileRoute('/_authed/_layout/pulls/')({
  loader: async ({ context: { getToken, userId } }) => {
    if (!userId) return;

    // OPEN Q: does this preloading work if we're using a different zero instance
    // than the one used by the component?
    const currentOrg = getCurrentOrg()!;

    const zero = createZeroInstance(userId, getToken);
    return zero.query.PullsTable.where('owner', currentOrg.account.login)
      .orderBy('state', 'desc')
      .orderBy('updatedAt', 'desc')
      .limit(PAGE_SIZE)
      .preload();
  },
  component: () => (
    <ErrorBoundary fallback={<div className="p-4">Error loading pull requests</div>}>
      <Suspense
        fallback={
          <div className="h-full w-full flex items-center justify-center">
            <div className="text-muted-foreground">Loading pull requests...</div>
          </div>
        }
      >
        <PullListPage />
      </Suspense>
    </ErrorBoundary>
  ),
});

const StickyIndexContext = createContext({
  stickyIndexes: [] as number[],
  activeIndex: -1,
});

const StickyItem = memo(({ children, style, index, ref }: CustomItemComponentProps) => {
  const { stickyIndexes, activeIndex } = useContext(StickyIndexContext);
  return (
    <div
      ref={ref}
      style={{
        ...style,
        ...(stickyIndexes.includes(index) && {
          zIndex: 1,
        }),
        ...(activeIndex === index && {
          position: 'sticky',
          top: 0,
        }),
      }}
    >
      {children}
    </div>
  );
});

function PullListPage() {
  const { currentOrg } = useCurrentOrg()!;
  const { user } = useUser();
  const z = useZero();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const { filters, setFilters, currentViewId, loadView: loadViewWithTouch } = usePullsFilters();
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterBadgeRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Saved views functionality
  const { savedViews, saveView, deleteView } = useSavedViews();
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [tempFilters, setTempFilters] = useState<typeof filters>([]);

  // useEffect(() => {
  //   api.github.diffs
  //     .$get()
  //     .then((r) => r.json())
  //     .then(console.log);
  // }, []);
  // Memoize the query to ensure it's stable across renders
  const query = useMemo(() => {
    let baseQuery = z.query.PullsTable.where('owner', currentOrg.account.login)
      .orderBy('state', 'desc')
      .orderBy('updatedAt', 'desc')
      .where('title', 'ILIKE', `%${searchTerm}%`);

    for (const filter of filters) {
      // Skip empty filters
      if (filter.values.length === 0) continue;

      // Skip author filter since we can't query on author.login - we'll
      // filter it in memory
      if (filter.field === 'author') continue;
      // Skip reviewer filter since we can't query on requestedReviewers array - we'll
      // filter it in memory
      if (filter.field === 'reviewer') continue;
      // Skip ready-to-merge filter since it's a computed property - we'll
      // filter it in memory
      if (filter.field === 'readyToMerge') continue;
      baseQuery = baseQuery.where(filter.field, filter.operation, filter.values);
    }

    return baseQuery.limit(PAGE_SIZE);
  }, [searchTerm, currentOrg, filters]);

  const [pullSummaries] = useZeroQuery(query);

  const authorFilter = useMemo(() => {
    return filters.find((f) => f.field === 'author');
  }, [filters]);

  const reviewerFilter = useMemo(() => {
    return filters.find((f) => f.field === 'reviewer');
  }, [filters]);

  const readyToMergeFilter = useMemo(() => {
    return filters.find((f) => f.field === 'readyToMerge');
  }, [filters]);

  // Filter author and reviewer in memory since we can't query them directly
  const filteredPullSummaries = useMemo(() => {
    let filtered = pullSummaries;

    if (authorFilter) {
      const { values, operation } = authorFilter;
      filtered = filtered.filter((summary) => {
        const author = summary.author?.login || '';
        if (operation === 'IN') {
          return values.includes(author);
        } else {
          return !values.includes(author);
        }
      });
    }

    if (reviewerFilter) {
      const { values, operation } = reviewerFilter;
      filtered = filtered.filter((summary) => {
        const reviewers = summary.requestedReviewers || [];
        const reviewerLogins = reviewers.map((r: any) => r?.login || '').filter(Boolean);
        if (operation === 'IN') {
          // Pull has at least one of the selected reviewers
          return values.some((value) => reviewerLogins.includes(value));
        } else {
          // Pull has none of the selected reviewers
          return !values.some((value) => reviewerLogins.includes(value));
        }
      });
    }

    if (readyToMergeFilter) {
      const { values, operation } = readyToMergeFilter;
      filtered = filtered.filter((summary) => {
        const isReady =
          summary.state === 'OPEN' &&
          (summary.reviewDecision === 'APPROVED' || summary.reviewDecision === null) &&
          summary.mergeable === true &&
          summary.checkStatusRollup === 'SUCCESS';

        const wantsReady = values.includes('true');
        if (operation === 'IN') {
          return wantsReady ? isReady : !isReady;
        } else {
          return wantsReady ? !isReady : isReady;
        }
      });
    }

    return filtered;
  }, [pullSummaries, authorFilter, reviewerFilter, readyToMergeFilter]);

  const [allPulls] = useZeroQuery(z.query.PullsTable.where('owner', currentOrg.account.login));

  const repositories = useMemo(() => {
    if (!Array.isArray(allPulls)) return [];
    const uniqRepos = new Set(allPulls.map((pull) => pull.repo));
    return Array.from(uniqRepos).sort();
  }, [allPulls]);

  // Get unique authors with their info
  const authors = useMemo(() => {
    if (!Array.isArray(allPulls)) return [];
    const seenLogins = new Set<string>();
    const authors: { login: string; avatarUrl?: string; name?: string | null }[] = [];
    for (const pull of allPulls) {
      if (pull.author?.login && !seenLogins.has(pull.author.login)) {
        seenLogins.add(pull.author.login);
        authors.push({
          login: pull.author.login,
          avatarUrl: pull.author.avatarUrl,
          name: pull.author.name,
        });
      }
    }

    return authors.sort((a, b) => a.login.localeCompare(b.login));
  }, [allPulls]);

  // Get unique reviewers with their info
  const reviewers = useMemo(() => {
    if (!Array.isArray(allPulls)) return [];
    const seenLogins = new Set<string>();
    const reviewers: { login: string; avatarUrl?: string | undefined }[] = [];
    for (const pull of allPulls) {
      if (pull.requestedReviewers && Array.isArray(pull.requestedReviewers)) {
        for (const reviewer of pull.requestedReviewers) {
          if (reviewer?.login && !seenLogins.has(reviewer.login)) {
            seenLogins.add(reviewer.login);
            reviewers.push({
              login: reviewer.login,
              avatarUrl: reviewer.avatarUrl || undefined,
            });
          }
        }
      }
    }

    return reviewers.sort((a, b) => a.login.localeCompare(b.login));
  }, [allPulls]);

  // Query for recent branch pushes for the current user
  const branchPushQuery = useMemo(() => {
    // Get recent pushes ordered by date, filter by username in memory (case-insensitive)
    return z.query.BranchPushesTable.where('owner', currentOrg.account.login)
      .where('pullRequestCreated', false)
      .where('dismissed', false)
      .orderBy('pushedAt', 'desc')
      .limit(5); // Get the 5 most recent pushes
  }, [currentOrg.account.login, z]);

  const [branchPushes] = useZeroQuery(branchPushQuery);

  // Group pulls by state
  const groupedPulls = useMemo(() => {
    const groups: Record<string, PullRecord[]> = {};
    for (const pull of filteredPullSummaries) {
      const state = pull.state;
      if (!groups[state]) groups[state] = [];
      groups[state].push(pull as PullRecord);
    }

    const sortedGroups: Record<string, PullRecord[]> = {};
    if (groups.OPEN) sortedGroups.OPEN = groups.OPEN;
    if (groups.DRAFT) sortedGroups.DRAFT = groups.DRAFT;
    if (groups.MERGED) sortedGroups.MERGED = groups.MERGED;
    if (groups.CLOSED) sortedGroups.CLOSED = groups.CLOSED;
    return sortedGroups;
  }, [filteredPullSummaries]);

  // Create flattened array with sticky headers
  const items = useMemo(() => {
    const result: Array<{ type: 'header' | 'row'; data: any }> = [];
    Object.entries(groupedPulls).forEach(([state, pulls]) => {
      result.push({ type: 'header', data: state });
      pulls.forEach((pull) => result.push({ type: 'row', data: pull }));
    });
    return result;
  }, [groupedPulls]);

  // Initialize active index to first row item
  const firstRowIndex = useMemo(() => items.findIndex((item) => item.type === 'row'), [items]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(firstRowIndex >= 0 ? firstRowIndex : null);

  const stickyIndexes = useMemo(() => {
    const result: number[] = [];
    items.forEach((item, index) => {
      if (item.type === 'header') {
        result.push(index);
      }
    });
    return result;
  }, [items]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Define filter focus handler
  const handleFocusFirstFilter = useCallback(() => {
    if (filters.length > 0) {
      const firstFilterField = filters[0].field;
      const firstFilterEl = filterBadgeRefs.current.get(firstFilterField);
      firstFilterEl?.focus();
    }
  }, [filters]);

  const handleFilterBadgeRef = useCallback(
    (field: string, el: HTMLDivElement | null) => {
      if (el) {
        filterBadgeRefs.current.set(field, el);
        // Add blur event listener to restore list focus
        el.addEventListener('blur', (e) => {
          // Check if focus is moving to another filter badge
          const nextTarget = (e as FocusEvent).relatedTarget as HTMLElement;
          const isAnotherFilterBadge =
            nextTarget && Array.from(filterBadgeRefs.current.values()).includes(nextTarget as HTMLDivElement);

          if (!isAnotherFilterBadge && !nextTarget?.closest('[role="menu"]')) {
            // Focus is leaving filters entirely, restore selected index
            if (selectedIndex !== null && listRef.current) {
              // Small delay to ensure DOM is ready
              setTimeout(() => {
                listRef.current?.scrollToIndex(selectedIndex);
                setSelectedIndex(selectedIndex);
              }, 0);
            }
          }
        });
      } else {
        filterBadgeRefs.current.delete(field);
      }
    },
    [selectedIndex]
  );

  const listRef = useRef<VListHandle>(null);
  // Only enable pull list shortcuts when filter dropdown is closed
  usePullListShortcuts({
    listRef,
    items,
    selectedIndex,
    setSelectedIndex,
    onOpenFilters: () => setFilterDropdownOpen(true),
    onFocusFilters: handleFocusFirstFilter,
    enabled: !filterDropdownOpen,
    searchInputRef,
  });

  // Search shortcut - active when dropdown is closed

  // Get branches that have open PRs for the current user
  const openPullBranches = useMemo(() => {
    const branches = new Set<string>();
    filteredPullSummaries
      .filter((pr) => (pr.state === 'OPEN' || pr.state === 'DRAFT') && pr.author?.login === user?.username)
      .forEach((pr) => {
        branches.add(`${pr.repo}:${pr.head.ref}`);
      });
    return branches;
  }, [filteredPullSummaries, user?.username]);

  // Filter suggestions to exclude branches with open PRs and default branches
  const suggestablePushes = useMemo(() => {
    if (!branchPushes || !user?.username) return [];

    const twoHoursAgo = Date.now() - 2 * 60 * 60 * 1000; // 2 hours in milliseconds

    return branchPushes
      .filter((push) => push.pusherLogin.toLowerCase() === user.username!.toLowerCase())
      .filter((push) => !openPullBranches.has(`${push.repo}:${push.branch}`))
      .filter((push): push is typeof push & { defaultBranch: string } => push.defaultBranch !== null) // Type guard to ensure defaultBranch is string
      .filter((push) => push.branch !== push.defaultBranch)
      .filter((push) => push.pushedAt >= twoHoursAgo) // Only show if pushed within last 2 hours
      .slice(0, 1); // Only show the most recent branch
  }, [branchPushes, openPullBranches, user?.username]);

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <div className="flex w-full items-center justify-between h-14 shrink-0 px-5 py-2 border-b border-stone-300">
        <div className="flex items-center gap-3">
          <SavedViewsDropdown
            savedViews={savedViews}
            currentViewId={currentViewId}
            onLoadView={loadViewWithTouch}
            onDeleteView={async (viewId) => {
              await deleteView(viewId);
              // If we deleted the current view, reset to all pulls
              if (viewId === currentViewId) {
                loadViewWithTouch('');
              }
            }}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => {
              setIsCreatingView(true);
              setTempFilters(filters);
            }}
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
          {!isCreatingView && (
            <PullsFilter
              repositories={repositories}
              authors={authors}
              reviewers={reviewers}
              filters={filters}
              setFilters={setFilters}
              open={filterDropdownOpen}
              onOpenChange={setFilterDropdownOpen}
              onFilterBadgeRef={handleFilterBadgeRef}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <DebouncedInput
              ref={searchInputRef}
              debounce={50}
              placeholder="Type / to search pull requests"
              initialValue={searchTerm}
              onChange={(value) => {
                setSearchTerm(String(value));
              }}
              className="w-82 pl-9"
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => {
              navigate({ to: '/pulls/new' });
            }}
          >
            <PlusIcon className="size-4" />
            New
          </Button>
        </div>
      </div>

      {/* Save view creator */}
      {isCreatingView && (
        <SavedViewCreator
          filters={tempFilters}
          setFilters={setTempFilters}
          repositories={repositories}
          authors={authors}
          reviewers={reviewers}
          onSave={async (name) => {
            await saveView(name, tempFilters);
            setIsCreatingView(false);
            const viewId = name.toLowerCase().replace(/\s+/g, '-');
            // Load the view with the filters directly to update currentViewId immediately
            loadViewWithTouch(viewId, tempFilters);
          }}
          onCancel={() => {
            setIsCreatingView(false);
            setTempFilters([]);
          }}
        />
      )}

      {/* Branch push suggestion banner */}
      {suggestablePushes.length > 0 && (
        <div className="px-5 pt-3">
          <PullSuggestionBanner suggestion={suggestablePushes[0]} />
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {items.length === 0 ? (
          <PullListNoResults searchTerm={searchTerm} hasFilters={filters.length > 0} />
        ) : (
          <StickyIndexContext.Provider value={{ stickyIndexes, activeIndex }}>
            <VList
              ref={listRef}
              keepMounted={activeIndex >= 0 && activeIndex < items.length ? [activeIndex] : []}
              onScroll={() => {
                if (!listRef.current) return;

                const start = listRef.current.findStartIndex();
                const activeStickyIndex = [...stickyIndexes].reverse().find((index) => start >= index)!;
                setActiveIndex(activeStickyIndex);
              }}
              item={StickyItem}
              className="h-full"
            >
              {items.map(({ type, data }, index) =>
                type === 'header' ? (
                  <div
                    key={index}
                    className={cn('flex items-center gap-2.5 bg-muted px-8 h-10 text-sm border-b border-border', {
                      'border-t': index !== activeIndex,
                      // Some fancy subtle gradient to accent the state of active group
                      'bg-linear-to-r from-emerald-50/20 to-muted to-75%': data === 'OPEN' && index === activeIndex,
                      'bg-linear-to-r from-indigo-50/30 to-muted to-75%': data === 'MERGED' && index === activeIndex,
                      'bg-linear-to-r from-stone-50/30 to-muted to-75%': data === 'CLOSED' && index === activeIndex,
                      'bg-linear-to-r from-gray-50/25 to-muted to-75%': data === 'DRAFT' && index === activeIndex,
                    })}
                  >
                    <StateCell state={data} size={14} tooltipEnabled={false} />
                    <div className="flex items-center gap-2">
                      <span>{stateDisplayName(data)}</span>
                      <span className="text-muted-foreground">
                        {groupedPulls[data as 'OPEN' | 'CLOSED' | 'MERGED' | 'DRAFT']?.length}
                      </span>
                    </div>
                  </div>
                ) : (
                  <Row key={index} pullSummary={data} isActive={selectedIndex === index} />
                )
              )}
            </VList>
          </StickyIndexContext.Provider>
        )}
      </div>
    </div>
  );
}

const Row = memo(({ pullSummary, isActive }: { pullSummary: PullRecord; isActive: boolean }) => {
  return (
    <MousedownLink
      to="/$owner/$repo/pull/$number"
      params={{ owner: pullSummary.owner, repo: pullSummary.repo, number: pullSummary.number }}
      preload={'intent'}
      resetScroll={true}
      className={cn(
        'w-full h-10 grid grid-cols-[120px_25px_1fr_140px_50px_50px_50px_30px] items-center content-center px-8 hover:bg-accent cursor-pointer',
        { 'bg-slate-200/60': isActive }
      )}
    >
      <div className="col-span-1 font-md text-sm truncate text-muted-foreground inline-flex items-center pr-4">
        <span className="truncate">{pullSummary.repo}</span>
        <span className="shrink-0">-{pullSummary.number}</span>
      </div>
      <div className="col-span-1 flex size-full items-center">
        <StateCell state={pullSummary.state} size={14} />
      </div>
      <div className="col-span-1 flex size-full items-center">
        <TitleCell title={pullSummary.title} />
      </div>
      <div className="col-span-1 flex justify-center items-center">
        <ChangesCell deletions={pullSummary.deletions} additions={pullSummary.additions} />
      </div>
      <div className="col-span-1">
        <CIStatusIcon owner={pullSummary.owner} repo={pullSummary.repo} sha={pullSummary.head.sha} />
      </div>
      <div className="col-span-1">
        <ReviewStatusCell reviewDecision={pullSummary.reviewDecision} />
      </div>
      <div className="col-span-1">
        <p className="text-xs">{getTimeAgo(pullSummary.updatedAt)}</p>
      </div>
      <div className="col-span-1 justify-self-end">
        <AuthorCell avatarUrl={pullSummary.author?.avatarUrl} login={pullSummary.author?.login} />
      </div>
    </MousedownLink>
  );
});
